import React, { useState } from 'react';
import { ShoppingCart, Check, CreditCard, Truck, Package, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { bookInfo, createMockOrder } from '../data/mockData';
import { Link } from 'react-router-dom';

const Comprar = () => {
  const [step, setStep] = useState(1); // 1: Info, 2: Shipping, 3: Payment, 4: Confirmation
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    region: '',
    zipCode: '',
    quantity: 1,
    shipping: 'standard',
    paymentMethod: 'credit-card'
  });
  const [order, setOrder] = useState(null);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleQuantityChange = (delta) => {
    const newQuantity = Math.max(1, formData.quantity + delta);
    setFormData({
      ...formData,
      quantity: newQuantity
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (step < 3) {
      setStep(step + 1);
    } else {
      // Mock order creation
      const mockOrder = createMockOrder(formData);
      setOrder(mockOrder);
      setStep(4);
    }
  };

  const subtotal = bookInfo.price * formData.quantity;
  const shippingCost = formData.shipping === 'express' ? 5990 : 2990;
  const total = subtotal + shippingCost;

  if (step === 4 && order) {
    return (
      <div className="min-h-screen pt-20 flex items-center justify-center brick-texture">
        <div className="container mx-auto px-4 py-20">
          <div className="max-w-2xl mx-auto">
            <div className="glass-morphism rounded-lg p-8 md:p-12 text-center space-y-6">
              <div className="w-20 h-20 bg-muro-gold rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-muro-black" />
              </div>
              
              <h1 className="text-4xl font-bold text-muro-text">
                ¡Orden Confirmada!
              </h1>
              
              <div className="bg-muro-gold/10 rounded-lg p-6 border border-muro-gold/20">
                <p className="text-muro-gold text-sm font-semibold mb-2">
                  Número de Orden (Mock)
                </p>
                <p className="text-2xl font-bold text-muro-text tracking-wider">
                  {order.orderId}
                </p>
              </div>

              <div className="space-y-4 text-left border-t border-muro-gray-700 pt-6">
                <div className="flex justify-between">
                  <span className="text-muro-text-muted">Cliente:</span>
                  <span className="text-muro-text font-medium">{order.customer.name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muro-text-muted">Email:</span>
                  <span className="text-muro-text font-medium">{order.customer.email}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muro-text-muted">Cantidad:</span>
                  <span className="text-muro-text font-medium">{order.customer.quantity} libro(s)</span>
                </div>
                <div className="flex justify-between border-t border-muro-gray-700 pt-4">
                  <span className="text-muro-text-muted text-lg">Total:</span>
                  <span className="text-2xl font-bold text-gold-gradient">
                    ${order.total.toLocaleString('es-CL')} CLP
                  </span>
                </div>
              </div>

              <div className="bg-muro-dark/50 rounded-lg p-6 border border-muro-gray-700">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="w-5 h-5 text-muro-gold flex-shrink-0 mt-1" />
                  <div className="text-left">
                    <p className="text-muro-text font-semibold mb-2">
                      Esta es una compra simulada (Mock)
                    </p>
                    <p className="text-muro-text-muted text-sm">
                      No se ha procesado ningún pago real. Este es un demo del sistema de preventa. 
                      Recibirás un email de confirmación cuando el sistema esté completamente funcional.
                    </p>
                  </div>
                </div>
              </div>

              <div className="pt-6 space-y-3">
                <Link to="/">
                  <Button className="btn-gold w-full py-4 text-lg rounded-md">
                    Volver al Inicio
                  </Button>
                </Link>
                <button
                  onClick={() => {
                    setStep(1);
                    setOrder(null);
                    setFormData({
                      name: '',
                      email: '',
                      phone: '',
                      address: '',
                      city: '',
                      region: '',
                      zipCode: '',
                      quantity: 1,
                      shipping: 'standard',
                      paymentMethod: 'credit-card'
                    });
                  }}
                  className="text-muro-text-muted hover:text-muro-gold transition-colors text-sm"
                >
                  Hacer otra compra de prueba
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      {/* Hero */}
      <section className="py-12 bg-muro-dark border-b border-muro-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h1 className="text-4xl md:text-5xl font-bold text-muro-text mb-6">
              Preventa <span className="text-gold-gradient">El Muro Endler</span>
            </h1>
            
            {/* Steps Indicator */}
            <div className="flex items-center space-x-4">
              {[
                { num: 1, label: 'Información' },
                { num: 2, label: 'Envío' },
                { num: 3, label: 'Pago' }
              ].map((s) => (
                <div key={s.num} className="flex items-center">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold ${
                    step >= s.num 
                      ? 'bg-muro-gold text-muro-black' 
                      : 'bg-muro-gray-800 text-muro-text-muted'
                  }`}>
                    {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                  </div>
                  <span className={`ml-2 text-sm ${
                    step >= s.num ? 'text-muro-text' : 'text-muro-text-muted'
                  }`}>
                    {s.label}
                  </span>
                  {s.num < 3 && (
                    <div className={`w-12 h-0.5 mx-4 ${
                      step > s.num ? 'bg-muro-gold' : 'bg-muro-gray-800'
                    }`}></div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12 brick-texture">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Form */}
              <div className="lg:col-span-2">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Step 1: Personal Info */}
                  {step === 1 && (
                    <div className="glass-morphism rounded-lg p-6 space-y-4">
                      <h2 className="text-2xl font-bold text-muro-text mb-4">
                        Información Personal
                      </h2>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-muro-text-muted text-sm mb-2">
                            Nombre Completo *
                          </label>
                          <input
                            type="text"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-muro-gray-900 border border-muro-gray-700 rounded-md text-muro-text focus:outline-none focus:border-muro-gold"
                            placeholder="Juan Pérez"
                          />
                        </div>
                        <div>
                          <label className="block text-muro-text-muted text-sm mb-2">
                            Email *
                          </label>
                          <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-muro-gray-900 border border-muro-gray-700 rounded-md text-muro-text focus:outline-none focus:border-muro-gold"
                            placeholder="juan@ejemplo.com"
                          />
                        </div>
                        <div className="md:col-span-2">
                          <label className="block text-muro-text-muted text-sm mb-2">
                            Teléfono *
                          </label>
                          <input
                            type="tel"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-muro-gray-900 border border-muro-gray-700 rounded-md text-muro-text focus:outline-none focus:border-muro-gold"
                            placeholder="+56 9 1234 5678"
                          />
                        </div>
                      </div>
                      <Button type="submit" className="btn-gold w-full py-4 text-lg rounded-md mt-6">
                        Continuar a Envío
                      </Button>
                    </div>
                  )}

                  {/* Step 2: Shipping */}
                  {step === 2 && (
                    <div className="glass-morphism rounded-lg p-6 space-y-4">
                      <h2 className="text-2xl font-bold text-muro-text mb-4">
                        Información de Envío
                      </h2>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-muro-text-muted text-sm mb-2">
                            Dirección *
                          </label>
                          <input
                            type="text"
                            name="address"
                            value={formData.address}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-3 bg-muro-gray-900 border border-muro-gray-700 rounded-md text-muro-text focus:outline-none focus:border-muro-gold"
                            placeholder="Av. Las Condes 123"
                          />
                        </div>
                        <div className="grid md:grid-cols-3 gap-4">
                          <div>
                            <label className="block text-muro-text-muted text-sm mb-2">
                              Ciudad *
                            </label>
                            <input
                              type="text"
                              name="city"
                              value={formData.city}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 bg-muro-gray-900 border border-muro-gray-700 rounded-md text-muro-text focus:outline-none focus:border-muro-gold"
                              placeholder="Santiago"
                            />
                          </div>
                          <div>
                            <label className="block text-muro-text-muted text-sm mb-2">
                              Región *
                            </label>
                            <input
                              type="text"
                              name="region"
                              value={formData.region}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 bg-muro-gray-900 border border-muro-gray-700 rounded-md text-muro-text focus:outline-none focus:border-muro-gold"
                              placeholder="Metropolitana"
                            />
                          </div>
                          <div>
                            <label className="block text-muro-text-muted text-sm mb-2">
                              Código Postal *
                            </label>
                            <input
                              type="text"
                              name="zipCode"
                              value={formData.zipCode}
                              onChange={handleChange}
                              required
                              className="w-full px-4 py-3 bg-muro-gray-900 border border-muro-gray-700 rounded-md text-muro-text focus:outline-none focus:border-muro-gold"
                              placeholder="7550000"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-muro-text-muted text-sm mb-3">
                            Método de Envío *
                          </label>
                          <div className="space-y-3">
                            <label className="flex items-center space-x-3 p-4 bg-muro-gray-900 border border-muro-gray-700 rounded-md cursor-pointer hover:border-muro-gold transition-colors">
                              <input
                                type="radio"
                                name="shipping"
                                value="standard"
                                checked={formData.shipping === 'standard'}
                                onChange={handleChange}
                                className="text-muro-gold"
                              />
                              <Truck className="w-5 h-5 text-muro-gold" />
                              <div className="flex-1">
                                <p className="text-muro-text font-medium">Envío Estándar</p>
                                <p className="text-muro-text-muted text-sm">5-7 días hábiles</p>
                              </div>
                              <span className="text-muro-text font-semibold">$2.990</span>
                            </label>
                            <label className="flex items-center space-x-3 p-4 bg-muro-gray-900 border border-muro-gray-700 rounded-md cursor-pointer hover:border-muro-gold transition-colors">
                              <input
                                type="radio"
                                name="shipping"
                                value="express"
                                checked={formData.shipping === 'express'}
                                onChange={handleChange}
                                className="text-muro-gold"
                              />
                              <Package className="w-5 h-5 text-muro-gold" />
                              <div className="flex-1">
                                <p className="text-muro-text font-medium">Envío Express</p>
                                <p className="text-muro-text-muted text-sm">2-3 días hábiles</p>
                              </div>
                              <span className="text-muro-text font-semibold">$5.990</span>
                            </label>
                          </div>
                        </div>
                      </div>
                      <div className="flex space-x-4 mt-6">
                        <Button 
                          type="button"
                          onClick={() => setStep(1)}
                          className="btn-gold-outline flex-1 py-4 text-lg rounded-md"
                        >
                          Volver
                        </Button>
                        <Button type="submit" className="btn-gold flex-1 py-4 text-lg rounded-md">
                          Continuar a Pago
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Step 3: Payment */}
                  {step === 3 && (
                    <div className="glass-morphism rounded-lg p-6 space-y-4">
                      <h2 className="text-2xl font-bold text-muro-text mb-4">
                        Información de Pago (Mock)
                      </h2>
                      
                      <div className="bg-muro-gold/10 rounded-lg p-4 border border-muro-gold/20 mb-6">
                        <div className="flex items-start space-x-3">
                          <AlertCircle className="w-5 h-5 text-muro-gold flex-shrink-0 mt-0.5" />
                          <p className="text-muro-text-muted text-sm">
                            Este es un sistema de pago simulado. No se procesará ningún cargo real.
                          </p>
                        </div>
                      </div>

                      <div>
                        <label className="block text-muro-text-muted text-sm mb-3">
                          Método de Pago *
                        </label>
                        <div className="space-y-3">
                          <label className="flex items-center space-x-3 p-4 bg-muro-gray-900 border border-muro-gray-700 rounded-md cursor-pointer hover:border-muro-gold transition-colors">
                            <input
                              type="radio"
                              name="paymentMethod"
                              value="credit-card"
                              checked={formData.paymentMethod === 'credit-card'}
                              onChange={handleChange}
                            />
                            <CreditCard className="w-5 h-5 text-muro-gold" />
                            <span className="text-muro-text">Tarjeta de Crédito/Débito</span>
                          </label>
                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <label className="block text-muro-text-muted text-sm mb-2">
                            Número de Tarjeta (Mock)
                          </label>
                          <input
                            type="text"
                            placeholder="4532 1234 5678 9010"
                            className="w-full px-4 py-3 bg-muro-gray-900 border border-muro-gray-700 rounded-md text-muro-text focus:outline-none focus:border-muro-gold"
                          />
                        </div>
                        <div>
                          <label className="block text-muro-text-muted text-sm mb-2">
                            Vencimiento (Mock)
                          </label>
                          <input
                            type="text"
                            placeholder="MM/AA"
                            className="w-full px-4 py-3 bg-muro-gray-900 border border-muro-gray-700 rounded-md text-muro-text focus:outline-none focus:border-muro-gold"
                          />
                        </div>
                        <div>
                          <label className="block text-muro-text-muted text-sm mb-2">
                            CVV (Mock)
                          </label>
                          <input
                            type="text"
                            placeholder="123"
                            className="w-full px-4 py-3 bg-muro-gray-900 border border-muro-gray-700 rounded-md text-muro-text focus:outline-none focus:border-muro-gold"
                          />
                        </div>
                      </div>

                      <div className="flex space-x-4 mt-6">
                        <Button 
                          type="button"
                          onClick={() => setStep(2)}
                          className="btn-gold-outline flex-1 py-4 text-lg rounded-md"
                        >
                          Volver
                        </Button>
                        <Button type="submit" className="btn-gold flex-1 py-4 text-lg rounded-md flex items-center justify-center space-x-2">
                          <ShoppingCart className="w-5 h-5" />
                          <span>Confirmar Compra Mock</span>
                        </Button>
                      </div>
                    </div>
                  )}
                </form>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                <div className="glass-morphism rounded-lg p-6 sticky top-24">
                  <h3 className="text-xl font-bold text-muro-text mb-4">
                    Resumen de Orden
                  </h3>
                  
                  <div className="space-y-4">
                    <div className="flex items-center space-x-4">
                      <img
                        src={bookInfo.coverImage}
                        alt="El Muro Endler"
                        className="w-20 h-28 object-cover rounded"
                      />
                      <div className="flex-1">
                        <h4 className="text-muro-text font-semibold mb-1">
                          El Muro Endler
                        </h4>
                        <p className="text-muro-text-muted text-sm">
                          Preventa Especial
                        </p>
                      </div>
                    </div>

                    <div className="border-t border-muro-gray-700 pt-4">
                      <div className="flex items-center justify-between mb-4">
                        <span className="text-muro-text-muted">Cantidad:</span>
                        <div className="flex items-center space-x-3">
                          <button
                            type="button"
                            onClick={() => handleQuantityChange(-1)}
                            className="w-8 h-8 bg-muro-gray-800 rounded-md text-muro-text hover:bg-muro-gold hover:text-muro-black transition-colors"
                          >
                            -
                          </button>
                          <span className="text-muro-text font-semibold w-8 text-center">
                            {formData.quantity}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleQuantityChange(1)}
                            className="w-8 h-8 bg-muro-gray-800 rounded-md text-muro-text hover:bg-muro-gold hover:text-muro-black transition-colors"
                          >
                            +
                          </button>
                        </div>
                      </div>

                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-muro-text-muted">Subtotal:</span>
                          <span className="text-muro-text font-medium">
                            ${subtotal.toLocaleString('es-CL')}
                          </span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muro-text-muted">Envío:</span>
                          <span className="text-muro-text font-medium">
                            ${shippingCost.toLocaleString('es-CL')}
                          </span>
                        </div>
                        <div className="flex justify-between border-t border-muro-gray-700 pt-2 mt-2">
                          <span className="text-muro-text font-semibold">Total:</span>
                          <span className="text-2xl font-bold text-gold-gradient">
                            ${total.toLocaleString('es-CL')}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="bg-muro-dark/50 rounded-lg p-4 border border-muro-gray-700 text-sm">
                      <p className="text-muro-text-muted">
                        <strong className="text-muro-gold">Lanzamiento:</strong> Junio 2026
                      </p>
                      <p className="text-muro-text-muted mt-2">
                        Tu libro será enviado apenas esté disponible.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Comprar;
